import {
  Backdrop,
  Box,
  CircularProgress,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchItemMasterAndCategoryMaster } from '../../redux/reducers/DashboardSlice';
import arrayToTree from 'array-to-tree';
import AddCategory from '../modals/AddCategory';
import AddNewItemModal from '../../pages/PrimeFigma/Modals/AddNewItemModal';
import { MoneyFormatv3 } from '../../library/MoneyFormat';
import ListingActive from '../modals/ListingActive';
import { API, graphqlOperation } from 'aws-amplify';
import { showErrorSnackbar, showSuccessSnackbar, showWarningSnackbar } from '../snackBar/snakbar';
import DeleteGoodsItem from '../modals/DeleteGoodsItem';
import { useNavigate } from 'react-router-dom';
import { flattenTree } from '../../utils/common';
import { RecursiveTreeItem } from './RecursiveTreeItemNew';
import { useThemes } from '../Theme/theme';
import {
  dashboardApi,
  useFetchDashboardItemCategoryMasterQuery,
  useFetchDashboardItemWithStockQuery,
} from '../../redux/apis/dashboardApi';
import { createItemMaster, getItemmaster } from '../../graphql';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { setSelectedItem } from '../../redux/reducers/inventoryItemSelectionSlice';
import { setDbDateRange } from '../../redux/reducers/DashboardSlice';
import StockItemWiseSummary from '../../features/StockManagement/pages/StockItemWiseSummary';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Tooltip, IconButton } from '@mui/material';
import { stockManagementApi } from '../../features/StockManagement/apis/stockManagement.api';

function ItemNewTable({ expandButtonClicked, setItemRow, hideZeroBalance, hideGroupTotal }) {
  const { theme } = useThemes();

  const dispatch = useDispatch();

  const dashboardData = useSelector((state) => state?.DashboardDataReducer);

  const { startTimeStamp: dbStartDate, endTimeStamp: dbEndDate } = dashboardData?.pandlPeriod || {};

  const { data: itemMaster, isLoading: itemMasterLoading } = useFetchDashboardItemWithStockQuery(
    {
      starttimestamp: dbStartDate,
      endtimestamp: dbEndDate,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !dbStartDate || !dbEndDate,
    }
  );

  const { data: itemGoods, isLoading: itemGoodsLoading } = useFetchDashboardItemCategoryMasterQuery(
    { maincategory: 'Goods' },
    { refetchOnMountOrArgChange: true }
  );
  const { data: itemServices, isLoading: itemServicesLoading } = useFetchDashboardItemCategoryMasterQuery(
    { maincategory: 'Services' },
    { refetchOnMountOrArgChange: true }
  );

  const [rows, setRows] = useState([]);
  const [backupRows, setBackupRows] = useState([]);
  const [createGroupModal, setCreateGroupModal] = useState(false);
  const [activeValueModal, setActiveValueModal] = useState();
  const [deleteItemModal, setDeleteItemModal] = useState(false);
  const [itemModal, setItemModal] = useState(false);
  const [itemDetails, setItemDetails] = useState();
  const [createLedgerModal, setCreateLedgerModal] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const [mainCategory, setMainCategory] = useState('');
  const [newAddedCategoryId, setNewAddedCategoryId] = useState([]);
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);
  const [stockReportModal, setStockReportModal] = useState(false);
  const [stockReportData, setStockReportData] = useState(null);
  const [modalKey, setModalKey] = useState(0);
  const reportDateInitialized = useRef(false);


  const [totals, setTotals] = useState({
    totalAmount: 0,
    totalQty: 0,
  });

  const [parent, setParent] = useState('');
  const { saveSkipStatus } = useSelector((state) => state.companySipSaveReducer);
  const memoizedRows = useMemo(() => rows, [rows]);

  const rowVirtualizer = useWindowVirtualizer({
    count: memoizedRows.length,
    estimateSize: (index) => {
      const item = memoizedRows[index];
      // Services category might need slightly more space due to borders
      if (item?.category === 'Category' && item?.type === 'Services' && (item?.parent === null || item?.parent === 0)) {
        return 48; // Slightly larger for Services category with borders
      }
      return 45; // Standard size
    },
    overscan: 10,
    scrollMargin: 0,
    scrollPaddingStart: 0,
    scrollPaddingEnd: 0,
  });

  const markAsInActive = async (obj) => {
    setIsUpdatingItem(true);
    try {
      const res = await API.graphql(graphqlOperation(getItemmaster, { itemid: obj.id }));
      const itemData = res?.data?.getItemmaster?.[0] || {};
      const comments = typeof itemData?.remarks === 'string' ? itemData?.remarks.split('\n') : [];
      let masterData = {
        currencycd: itemData?.currencycd,
        hsnid: itemData?.hsncodevalue || '',
        itemid: itemData.itemid,
        itemcategorycd: Number(itemData.itemcategorycd),
        itemcode: itemData?.itemcode,
        itemname: itemData?.itemname,
        itemtype: itemData.itemtype,
        measurementcd: itemData.measurementcd,
        purchaseaccountid: itemData.purchaseaccountid,
        purchaseinvoiceflag: itemData.purchaseinvoiceflag,
        reversechargeflag: itemData?.reversechargeflag,
        salesaccountid: itemData.salesaccountid,
        salesinvoiceflag: itemData.salesinvoiceflag,
        taxabilitycd: Number(itemData?.taxabilitycd),
        is_active: itemData?.is_active ? 0 : 1,
        serialnumber: itemData?.serialnumber,
        expiredtimestamp: itemData.expiredtimestamp,
        lastupdatedby: itemData?.lastupdatedby,
        financialyearid: itemData?.financialyearid,
        companyid: itemData?.companyid,
        description: comments?.join('**'),
      };

      let response = await API.graphql(
        graphqlOperation(createItemMaster, {
          createItemMasterInput: masterData,
        })
      );

      if (obj.is_active && newAddedCategoryId.includes(obj.id)) {
        setNewAddedCategoryId(prev => prev.filter(id => id !== obj.id));
      }

      showSuccessSnackbar(`Item marked as ${!obj.is_active ? 'active' : 'inactive'}`);
      
      showSuccessSnackbar('Updating item list...');
      
      const refetchPromise = dispatch(dashboardApi.endpoints.fetchDashboardItemWithStock.initiate({
        starttimestamp: dbStartDate,
        endtimestamp: dbEndDate,
      }, { forceRefetch: true }));
      
      await refetchPromise;
      
      showSuccessSnackbar(`Item successfully ${!obj.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      if (error && error.errors[0]?.message.includes('Item Already Created')) {
        showWarningSnackbar('Item Cannot Be Deactivated As Invoice For Item Already Created');
      } else {
        showWarningSnackbar('Something went wrong');
      }
      console.log(error);
    } finally {
      setIsUpdatingItem(false);
    }
  };

  // Handle closing stock click to show Stock Ledger report in modal
  const handleClosingStockClick = (itemData) => {
    // First set the modal data
    setStockReportData({
      itemid: itemData.id, 
      itemname: itemData.name, 
      itemcode: itemData.itemcode,
      unit: itemData.unit || 'N/A',
    });

    // Dispatch the selected item to Redux store (simulates checkbox being ticked)
    dispatch(setSelectedItem({
      itemid: itemData.id, 
      itemname: itemData.name, 
      itemcode: itemData.itemcode,
      unit: itemData.unit
    }));
    
    // Ensure report dates are set for the modal by using current period settings
    // Use itemPeriod dates if available, otherwise use current financial year dates
    let range = {
      startTimeStamp: dbStartDate || null,
      endTimeStamp: dbEndDate || null,
    };
    
    // If no dates are available, set default current financial year dates
    if (!dbStartDate || !dbEndDate) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      let thisYear;
      
      if (currentMonth >= 4) {
        thisYear = currentYear;
      } else {
        thisYear = currentYear - 1;
      }
      
      // Set default to current financial year
      range = {
        startTimeStamp: `${thisYear}-04-01T00:00:00.000Z`,
        endTimeStamp: `${thisYear + 1}-03-31T23:59:59.999Z`,
      };
    }
    
    
    // Always update report date range with current dashboard period dates
    dispatch(
      setDbDateRange({
        outerKey: 'report',
        innerKey: 'dateRange',
        value: range,
      })
    );
    
    // Delay to ensure Redux state is fully updated before modal opens
    setTimeout(() => {
      // Invalidate RTK Query cache to ensure fresh data
      dispatch(stockManagementApi.util.invalidateTags(['listStockSummaryByItemId']));
      
      // Force component remount by changing key
      setModalKey(prev => prev + 1);
      setStockReportModal(true);
    }, 300);
  };


  useEffect(() => {
    getCategoryAndItems();
  }, [itemMaster, itemGoods, itemServices]);

  // Initialize report date range when component mounts or itemPeriod changes
  useEffect(() => {
    // Only set report date range if it's not already set and we haven't initialized yet
    const { report } = dashboardData || {};
    
    if (!reportDateInitialized.current && (!report?.dateRange?.startTimeStamp || !report?.dateRange?.endTimeStamp) && (dbStartDate || dbEndDate)) {
      const range = {
        startTimeStamp: dbStartDate,
        endTimeStamp: dbEndDate,
      };
      
      dispatch(
        setDbDateRange({
          outerKey: 'report',
          innerKey: 'dateRange',
          value: range,
        })
      );
      
      reportDateInitialized.current = true;
    }
  }, [dbStartDate, dbEndDate, dispatch, dashboardData]);


  async function getCategoryAndItems() {
    try {  
      const itemData = itemMaster.map((data) => {
        return {
          name: data?.itemname,
          category: 'Item',
          type: data?.itemtype,
          id: data?.itemid,
          itemcode: data?.itemcode,
          qty: data?.itemtype === 'SERVICE' ? '' : data?.closing_quantity || 0,
          price: data?.itemtype === 'SERVICE' ? '' : data?.closing_amount || 0,
          unit: data?.itemtype === 'SERVICE' ? '' : data?.unit || '',
          parent: data?.itemcategoryid,
          is_active: data?.is_active,
        };
      });
      
      const allCategories = [...itemGoods, ...itemServices];
      const categoryMap = {};
      allCategories.forEach((cat) => {
        categoryMap[cat.itemcategoryid] = {
          id: cat.itemcategoryid,
          name: cat.category,
          type: cat.maincategory,
          parent: cat.itemcategorygroupid === 0 ? null : cat.itemcategorygroupid,
          children: [],
          items: []
        };
      });
      
      itemData.forEach((item) => {
        if (item.parent && categoryMap[item.parent]) {
          categoryMap[item.parent].items.push(item);
        }
      });
      
      Object.values(categoryMap).forEach((cat) => {
        if (cat.parent && categoryMap[cat.parent]) {
          categoryMap[cat.parent].children.push(cat);
        }
      });
      
      const calculateCategoryTotals = (category) => {
        let totalQty = 0;
        let totalPrice = 0;
        
        category.items.forEach((item) => {
          totalQty += item.qty || 0;
          totalPrice += item.price || 0;
        });
        
        category.children.forEach((child) => {
          const childTotals = calculateCategoryTotals(child);
          totalQty += childTotals.qty;
          totalPrice += childTotals.price;
        });
        
        return { qty: totalQty, price: totalPrice };
      };
      
      const categoryTotals = {};
      Object.values(categoryMap).forEach((cat) => {
        const totals = calculateCategoryTotals(cat);
        categoryTotals[cat.id] = totals;
      });
      
      const categoryData = allCategories.map((data) => {
        const totals = categoryTotals[data.itemcategoryid] || { qty: 0, price: 0 };
        return {
          name: data.category,
          category: 'Category',
          type: data.maincategory,
          id: data.itemcategoryid,
          qty: data.maincategory === 'Services' ? '' : totals.qty,
          price: data.maincategory === 'Services' ? '' : totals.price,
          parent: data.itemcategorygroupid === 0 ? null : data.itemcategorygroupid,
        };
      });

      const combinedData = [...itemData, ...categoryData];

      const tree = arrayToTree(combinedData, {
        parentProperty: 'parent',
        customID: 'id',
      });

      const flatTree = flattenTree(tree, 'item');
      
      const recalculateCategoryTotals = (data) => {
        const filteredCategories = data.filter(item => item.category === 'Category');
        const filteredItems = data.filter(item => item.category === 'Item');
        
        const filteredCategoryMap = {};
        filteredCategories.forEach((cat) => {
          filteredCategoryMap[cat.id] = {
            id: cat.id,
            parent: cat.parent,
            children: [],
            items: []
          };
        });
        
        filteredItems.forEach((item) => {
          if (item.parent && filteredCategoryMap[item.parent]) {
            filteredCategoryMap[item.parent].items.push(item);
          }
        });
        
        Object.values(filteredCategoryMap).forEach((cat) => {
          if (cat.parent && filteredCategoryMap[cat.parent]) {
            filteredCategoryMap[cat.parent].children.push(cat);
          }
        });
        
        const calculateFilteredTotals = (category) => {
          let totalQty = 0;
          let totalPrice = 0;
          
          category.items.forEach((item) => {
            totalQty += item.qty || 0;
            totalPrice += item.price || 0;
          });
          
          category.children.forEach((child) => {
            const childTotals = calculateFilteredTotals(child);
            totalQty += childTotals.qty;
            totalPrice += childTotals.price;
          });
          
          return { qty: totalQty, price: totalPrice };
        };
        
        return data.map((item) => {
          if (item.category === 'Category') {
            const totals = calculateFilteredTotals(filteredCategoryMap[item.id]) || { qty: 0, price: 0 };
            return { 
              ...item, 
              qty: item.type === 'Services' ? '' : totals.qty, 
              price: item.type === 'Services' ? '' : totals.price 
            };
          }
          if (item.category === 'Item' && item.type === 'Service') {
            return { ...item, qty: '', price: '' };
          }
          return item;
        });
      };
      
      const finalData = recalculateCategoryTotals(flatTree);
      
      const totalQty = finalData.filter(item => item.category === 'Item').reduce((sum, node) => sum + (node.qty || 0), 0);
      const totalAmount = finalData.filter(item => item.category === 'Item').reduce((sum, node) => sum + (node.price || 0), 0);


      setTotals({
        totalQty,
        totalAmount,
      });


      setBackupRows(finalData);
      setItemRow(finalData);
    } catch (error) {
      console.error('Error item tree', error);
    }
  }

  useEffect(() => {
    if (expandButtonClicked) {
      expandAll();
    } else {
      collapseAll();
    }
  }, [expandButtonClicked, backupRows, hideZeroBalance]);

  function collapseAll() {
    let filteredData = [...backupRows];
    
    if (hideZeroBalance) {
      filteredData = filteredData.filter((item) => {
        if (item.category === 'Item') {
          return item.qty !== 0 || item.price !== 0;
        }
        return true;
      });
    }
    
    const recalculateCategoryTotals = (data) => {
      // Create category map from filtered data
      const filteredCategories = data.filter(item => item.category === 'Category');
      const filteredItems = data.filter(item => item.category === 'Item');
      
      const filteredCategoryMap = {};
      filteredCategories.forEach((cat) => {
        filteredCategoryMap[cat.id] = {
          id: cat.id,
          parent: cat.parent,
          children: [],
          items: []
        };
      });
      
      // Add items to their categories
      filteredItems.forEach((item) => {
        if (item.parent && filteredCategoryMap[item.parent]) {
          filteredCategoryMap[item.parent].items.push(item);
        }
      });
      
      // Build hierarchy
      Object.values(filteredCategoryMap).forEach((cat) => {
        if (cat.parent && filteredCategoryMap[cat.parent]) {
          filteredCategoryMap[cat.parent].children.push(cat);
        }
      });
      
      // Calculate hierarchical totals
      const calculateFilteredTotals = (category) => {
        let totalQty = 0;
        let totalPrice = 0;
        
        category.items.forEach((item) => {
          totalQty += item.qty || 0;
          totalPrice += item.price || 0;
        });
        
        category.children.forEach((child) => {
          const childTotals = calculateFilteredTotals(child);
          totalQty += childTotals.qty;
          totalPrice += childTotals.price;
        });
        
        return { qty: totalQty, price: totalPrice };
      };
      
      // Update category totals
      return data.map((item) => {
        if (item.category === 'Category') {
          const totals = calculateFilteredTotals(filteredCategoryMap[item.id]) || { qty: 0, price: 0 };
          return { 
            ...item, 
            qty: item.type === 'Services' ? '' : totals.qty, 
            price: item.type === 'Services' ? '' : totals.price 
          };
        }
        if (item.category === 'Item' && item.type === 'SERVICE') {
          return { ...item, qty: '', price: '' };
        }
        return item;
      });
    };
    
    const finalData = recalculateCategoryTotals(filteredData);
    
    const categories = finalData.filter((item) => item.category === 'Category');
    const collapsedState = categories.map((item) => ({
      id: item.id,
      isExpand: false,
    }));
    setExpandedItems(collapsedState);
    const collapseData = categories.filter((item) => [1, 2].includes(item.id));
    setRows(collapseData);
  }

  function expandAll() {
    let filteredData = [...backupRows];
    
    if (hideZeroBalance) {
      filteredData = filteredData.filter((item) => {
        if (item.category === 'Item') {
          return item.qty !== 0 || item.price !== 0;
        }
        return true;
      });
    }
    
    const recalculateCategoryTotals = (data) => {
      // Create category map from filtered data
      const filteredCategories = data.filter(item => item.category === 'Category');
      const filteredItems = data.filter(item => item.category === 'Item');
      
      const filteredCategoryMap = {};
      filteredCategories.forEach((cat) => {
        filteredCategoryMap[cat.id] = {
          id: cat.id,
          parent: cat.parent,
          children: [],
          items: []
        };
      });
      
      // Add items to their categories
      filteredItems.forEach((item) => {
        if (item.parent && filteredCategoryMap[item.parent]) {
          filteredCategoryMap[item.parent].items.push(item);
        }
      });
      
      // Build hierarchy
      Object.values(filteredCategoryMap).forEach((cat) => {
        if (cat.parent && filteredCategoryMap[cat.parent]) {
          filteredCategoryMap[cat.parent].children.push(cat);
        }
      });
      
      // Calculate hierarchical totals
      const calculateFilteredTotals = (category) => {
        let totalQty = 0;
        let totalPrice = 0;
        
        category.items.forEach((item) => {
          totalQty += item.qty || 0;
          totalPrice += item.price || 0;
        });
        
        category.children.forEach((child) => {
          const childTotals = calculateFilteredTotals(child);
          totalQty += childTotals.qty;
          totalPrice += childTotals.price;
        });
        
        return { qty: totalQty, price: totalPrice };
      };
      
      // Update category totals
      return data.map((item) => {
        if (item.category === 'Category') {
          const totals = calculateFilteredTotals(filteredCategoryMap[item.id]) || { qty: 0, price: 0 };
          return { 
            ...item, 
            qty: item.type === 'Services' ? '' : totals.qty, 
            price: item.type === 'Services' ? '' : totals.price 
          };
        }
        if (item.category === 'Item' && item.type === 'SERVICE') {
          return { ...item, qty: '', price: '' };
        }
        return item;
      });
    };
    
    const finalData = recalculateCategoryTotals(filteredData);
    
    const categories = finalData.filter((item) => item.category === 'Category');
    const expandedState = categories.map((item) => ({
      id: item.id,
      isExpand: true,
    }));
    setExpandedItems(expandedState);
    setRows(finalData);
  }


  const handleToggle = useCallback(
    (event, id) => {
      setRows((prevRows) => {
        const isRowExpanded = expandedItems.find((ele) => ele.id === id)?.isExpand || false;
        let updatedRows = [...prevRows];
        
        let filteredData = [...backupRows];
        if (hideZeroBalance) {
          filteredData = filteredData.filter((item) => {
            if (item.category === 'Item') {
              return item.qty !== 0 || item.price !== 0;
            }
            return true;
          });
        }
        
        if (isRowExpanded) {
          const collapseIDs = rows.filter((item) => item.path?.includes(`${id}`) && item.category === 'Category').map((item) => item.id);
          updatedRows = prevRows.filter((item) => !item.path?.includes(`${id}`) || (item.id === id && item.category === 'Category'));
          setExpandedItems((prev) => prev.map((row) => (collapseIDs.includes(row.id) ? { ...row, isExpand: false } : row)));
        } else {
          const childrenData = filteredData.filter((item) => item.parent == id);
          const index = prevRows.findIndex((item) => item.id === id);
          if (index !== -1) {
            updatedRows.splice(index + 1, 0, ...childrenData);
          } else {
            updatedRows = [...prevRows, ...childrenData];
          }
          setExpandedItems((prev) => prev.map((row) => (row.id === id ? { ...row, isExpand: true } : row)));
        }
        
        const recalculateCategoryTotals = (data) => {
          const filteredCategories = data.filter(item => item.category === 'Category');
          const filteredItems = data.filter(item => item.category === 'Item');
          
          const filteredCategoryMap = {};
          filteredCategories.forEach((cat) => {
            filteredCategoryMap[cat.id] = {
              id: cat.id,
              parent: cat.parent,
              children: [],
              items: []
            };
          });
          
          filteredItems.forEach((item) => {
            if (item.parent && filteredCategoryMap[item.parent]) {
              filteredCategoryMap[item.parent].items.push(item);
            }
          });
          
          Object.values(filteredCategoryMap).forEach((cat) => {
            if (cat.parent && filteredCategoryMap[cat.parent]) {
              filteredCategoryMap[cat.parent].children.push(cat);
            }
          });
          
          const calculateFilteredTotals = (category) => {
            let totalQty = 0;
            let totalPrice = 0;
            
            category.items.forEach((item) => {
              totalQty += item.qty || 0;
              totalPrice += item.price || 0;
            });
            
            category.children.forEach((child) => {
              const childTotals = calculateFilteredTotals(child);
              totalQty += childTotals.qty;
              totalPrice += childTotals.price;
            });
            
            return { qty: totalQty, price: totalPrice };
          };
          
          return data.map((item) => {
            if (item.category === 'Category') {
              const totals = calculateFilteredTotals(filteredCategoryMap[item.id]) || { qty: 0, price: 0 };
              return { ...item, qty: totals.qty, price: totals.price };
            }
            return item;
          });
        };
        
        return recalculateCategoryTotals(updatedRows);
      });
    },
    [backupRows, expandedItems, hideZeroBalance]
  );

  let themes = createTheme(theme);
  const borderRightColor = theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#eef0f0';
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));


  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={itemMasterLoading || itemGoodsLoading || itemServicesLoading || isUpdatingItem}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ThemeProvider theme={themes}>
        <Box sx={{ boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 2, padding: '10px' }}>
            <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, fontWeight: 'bold' }}>
              Total closing stock ₹ {MoneyFormatv3(totals.totalAmount)}
            </Typography>
          </Box>{' '}
          <Box sx={{width:'100%',overflow:'scroll'}}>
              <Box sx={{width:{xs:"800px",sm:"100%"}}}>
              <Box sx={{ display: 'flex', height: '50px', color: '#ffff' ,}}>
                <Box
                  sx={{
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    width: {xs:"45%", sm:"55%"},
                    backgroundColor: `${theme.palette.primary.main}  !important`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      height: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderRight: '4px solid ',
                      borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
                      borderTopLeftRadius: '10px',
                      borderTopRightRadius: '10px',
                    }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 'bold',
                        pl: { xs: 2, md: 4 },
                        color: '#FFF',
                        fontSize: { xs: '12px', md: '16px', sm: '16px' },
                      }}
                    >
                      Item
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: {xs:'10%',sm:'8%'},
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    backgroundColor: `${theme.palette.primary.main}  !important`,
                  }}
                >
                  <Box
                    sx={{
                      borderTopLeftRadius: '10px',
                      borderTopRightRadius: '10px',
                      height: '100%',
                      borderRight: '4px solid ',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
                    }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 'bold',
                        color: '#FFF',
                        fontSize: { xs: '10px', md: '14px', sm: '14px' },
                      }}
                    >
                      Unit
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: {xs:'20%',sm:'18%'},
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    backgroundColor: `${theme.palette.primary.main}  !important`,
                  }}
                >
                  <Box
                    sx={{
                      borderTopLeftRadius: '10px',
                      borderTopRightRadius: '10px',
                      height: '100%',
                      borderRight: '4px solid ',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
                    }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 'bold',
                        color: '#FFF',
                        fontSize: { xs: '10px', md: '14px', sm: '14px' },
                      }}
                    >
                      {isSmallScreen ? 'Stock' : 'No of units in hand'}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: {xs:'25%',sm:'19%'},
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: `${theme.palette.primary.main}  !important`,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '10px', md: '14px', sm: '14px' } }}>Total value</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  height: `${Math.max(rowVirtualizer.getTotalSize(), memoizedRows.length * 45)}px`,
                  borderRight: '1px solid #C0C0C0',
                  borderLeft: '1px solid #C0C0C0',
                  position: 'relative',
                  willChange: 'transform',
                  contain: 'layout style paint',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const item = memoizedRows[virtualRow.index];
                  if (!item) return null;
                  
                  const isExpand = expandedItems.find((ele) => ele.id === item?.id)?.isExpand || false;
                  
                  // Check if this is the main Services category (top-level, not nested)
                  const isMainServicesCategory = item?.category === 'Category' && 
                    item?.type === 'Services' && 
                    (item?.parent === null || item?.parent === 0);
                  
                  // Check if this is the first Services category to add top border
                  const isFirstServicesCategory = isMainServicesCategory && 
                    virtualRow.index > 0 && 
                    memoizedRows[virtualRow.index - 1]?.type !== 'Services';
                  
                  return (
                    <div
                      key={virtualRow.key}
                      style={{
                        position: 'absolute',
                        top: virtualRow.start,
                        left: 0,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        height: `45px`,
                        borderBottom: isMainServicesCategory ? '2px solid #d0d0d0' : '1px solid #C0C0C0',
                        borderTop: isFirstServicesCategory ? '3px solid #b0b0b0' : 'none',
                        backgroundColor: isMainServicesCategory ? '#f8f9fa' : 'transparent',
                        boxSizing: 'border-box',
                        willChange: 'transform',
                        contain: 'layout style paint',
                      }}
                    >
                      <RecursiveTreeItem
                        row={item}
                        isExpand={isExpand}
                        borderRightColor={borderRightColor}
                        setItemModal={setItemModal}
                        setItemDetails={setItemDetails}
                        setParent={setParent}
                        setCreateLedgerModal={setCreateLedgerModal}
                        setMainCategory={setMainCategory}
                        setCreateGroupModal={setCreateGroupModal}
                        setActiveValueModal={setActiveValueModal}
                        setDeleteItemModal={setDeleteItemModal}
                        newAddedCategoryId={newAddedCategoryId}
                        expandedItems={expandedItems}
                        saveSkipStatus={saveSkipStatus}
                        handleToggle={handleToggle}
                        hideGroupTotal={hideGroupTotal}
                        isUpdatingItem={isUpdatingItem}
                        isServicesSection={isMainServicesCategory}
                        handleClosingStockClick={handleClosingStockClick}
                      />
                    </div>
                  );
                })}
              </Box>
          </Box>

          </Box>
          
          
        </Box>
        {/* <TableContainer
          className="custom-scrollbar"
          sx={{
            height: {
              sm: "calc( 100vh - 280px )",
              xs: "calc( 100vh - 300px )",
            },
          }}
        >
          <Table aria-label="recursive table">
            <TableHead>
              <TableCell
                sx={{
                  borderBottom: "1px solid",
                  borderBottomColor: "primary.main",
                  backgroundColor: "primary.main",
                  borderTopRightRadius: 10,
                  borderTopLeftRadius: 10,
                  width: "70%",
                  padding: "20px 8px",
                  color: "#818694",
                  borderLeftColor: "primary.main",
                  borderTopRightRadius: 15,
                  borderRight: "4px solid ",
                  borderRightColor: theme?.palette?.TmId.id
                    .toString()
                    .includes("9")
                    ? "#26262F"
                    : "#ffffff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "#ffff",
                    fontFamily: "overpass",
                    letterSpacing: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                    Item
                  </Typography>
                  <Typography sx={{ fontSize: { xs: "12px", sm: "auto" } }}>
                    Total closing stock ₹ {MoneyFormatv3(totals?.totalAmount || 0)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  borderBottom: "1px solid",
                  borderBottomColor: "primary.main",
                  backgroundColor: "primary.main",
                  borderTopRightRadius: 10,
                  borderTopLeftRadius: 10,
                  padding: "20px 8px",
                  color: "#818694",
                  borderLeftColor: "primary.main",
                  borderTopRightRadius: 15,
                  borderRight: "4px solid ",
                  borderRightColor: theme?.palette?.TmId.id
                    .toString()
                    .includes("9")
                    ? "#26262F"
                    : "#ffffff",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: "100%",
                      color: "#FFF",
                      // fontSize: 13,
                      // fontWeight: '700',
                      letterSpacing: 1,
                      display: { sm: "flex", xs: "none" },
                      flexDirection: "row?",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 15,
                      fontFamily: "overpass",
                      fontSize: 15,
                    }}
                  >
                    No of units in hand
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      color: "#FFF",
                      // fontSize: 13,
                      // fontWeight: '700',
                      letterSpacing: 1,
                      display: { xs: "flex", sm: "none" },
                      flexDirection: "row?",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 15,
                      fontFamily: "overpass",
                      fontSize: 15,
                    }}
                  >
                    Qty
                  </Box>
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "primary.main",
                  borderTopRightRadius: 10,
                  borderTopLeftRadius: 10,
                  position: "relative",
                  borderTopLeftRadius: 15,
                  borderLeftColor: theme?.palette?.TmId.id
                    .toString()
                    .includes("9")
                    ? "#26262F"
                    : "#EEEEFA",
                }}
              >
                <Box>
                  <div
                    style={{
                      width: "100%",
                      color: "#FFF",
                      // fontSize: 13,
                      // fontWeight: '700',
                      letterSpacing: 1,
                      display: "flex",
                      flexDirection: "row?",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 15,
                      fontFamily: "overpass",
                      fontSize: 15,
                    }}
                  >
                    Total value
                  </div>
                </Box>
              </TableCell>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <RecursiveTreeItem
                  row={row}
                  setItemModal={setItemModal}
                  setItemDetails={setItemDetails}
                  setParent={setParent}
                  setCreateLedgerModal={setCreateLedgerModal}
                  setMainCategory={setMainCategory}
                  setCreateGroupModal={setCreateGroupModal}
                  setActiveValueModal={setActiveValueModal}
                  setDeleteItemModal={setDeleteItemModal}
                  newAddedCategoryId={newAddedCategoryId}
                  expandedItems={expandedItems}
                  saveSkipStatus={saveSkipStatus}
                  handleToggle={handleToggle}
                  hideGroupTotal={hideGroupTotal}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer> */}
      </ThemeProvider>
      {createGroupModal && (
        <AddCategory
          MainParent={mainCategory}
          IDparent={parent}
          setCheckedLabel={() => {}}
          setStateS={setNewAddedCategoryId}
          open={createGroupModal}
          setOpen={setCreateGroupModal}
          dataArr={mainCategory === 'Goods' ? itemGoods : itemServices}
          listCategory={() => {}}
          // setCategoryData={setCategoryData}
        />
      )}

      {createLedgerModal === true && (
        <AddNewItemModal
          categoryId={parent}
          open={createLedgerModal}
          setOpen={setCreateLedgerModal}
          modalEvent={true}
          setItemID={(value) => setNewAddedCategoryId([value])}
          itemID={''}
          itemType={mainCategory == 'Services' ? 'SERVICE' : 'GOODS'}
          page={'Dashboard'}
        />
      )}
      {activeValueModal ? (
        <ListingActive
          text={'Item'}
          open={activeValueModal}
          editData={{ serialisactive: activeValueModal?.is_active }}
          setOpen={setActiveValueModal}
          action={() => {
            markAsInActive(activeValueModal);
          }}
        />
      ) : null}
      {deleteItemModal == true ? (
        <DeleteGoodsItem
          open={deleteItemModal}
          setOpen={setDeleteItemModal}
          itemData={itemDetails}
          listItemDetails={() => dispatch(fetchItemMasterAndCategoryMaster())}
        />
      ) : null}
      {itemModal == true ? (
        <AddNewItemModal
          open={itemModal}
          setOpen={(value) => {
            setNewAddedCategoryId([]);
            setItemModal(value);
          }}
          modalEvent={true}
          setBackdropOpen={setBackdropOpen}
          itemID={itemDetails.itemId}
          itemName={itemDetails.name}
          itemType={itemDetails.type}
          view={itemDetails.view}
          // setCurrentItemCode={setCurrentItemCode}
        />
      ) : null}

      {/* Stock Ledger Modal */}
      {stockReportModal && stockReportData && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              width: '98%',
              height: '96%',
              maxWidth: '1800px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              }}
            >
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                Item Stock Ledger Report (FIFO Method) - {stockReportData.itemname}
              </Typography>
              <Tooltip title="Close">
                <IconButton
                  onClick={() => {
                    setStockReportModal(false);
                    setStockReportData(null);
                  }}
                  sx={{ 
                    color: '#64748b',
                    '&:hover': { backgroundColor: '#e2e8f0' }
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Modal Content */}
            <Box sx={{ flex: 1, overflow: 'auto', padding: '0 20px 20px 20px' }}>
              <StockItemWiseSummary 
                key={`modal-${stockReportData?.itemid}-${modalKey}`}
                itemDetails={stockReportData}
                variant="modal"
                startTimeStamp={dbStartDate}
                endTimeStamp={dbEndDate}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

export default memo(ItemNewTable);
